import React, { useState } from "react";
import { Form, ProgressBar, Button, Alert } from "react-bootstrap";
import FormContainer from "../components/FormContainer";
import Loader from "../components/Loader";
import { useSearchParams, Link } from "react-router-dom";
import { useResetForgottenPasswordMutation } from "../slices/usersApiSlices";
import { CiCircleCheck } from "react-icons/ci";

const ResetForgottenPasswordScreen = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const [resetPassword, { isLoading }] = useResetForgottenPasswordMutation();

  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = searchParams.get("token");

  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score += 20;
    if (/[A-Z]/.test(pwd)) score += 20;
    if (/[a-z]/.test(pwd)) score += 20;
    if (/\d/.test(pwd)) score += 20;
    if (/[!@#$%^&*.]/.test(pwd)) score += 20;
    return score;
  };

  const getPasswordVariant = (score) => {
    if (score < 40) return "danger";
    if (score < 80) return "warning";
    return "success";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !token) {
      setErrorMessage("Reset link is invalid or incomplete.");
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*.])[A-Za-z\d!@#$%^&*.]{8,}$/;

    if (!passwordRegex.test(password)) {
      setErrorMessage(
        "Password must be at least 8 characters long and include uppercase, lowercase, a number, and a special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      await resetPassword({ email, password, token }).unwrap();
      setResetSuccess(true);
      setErrorMessage("");
    } catch (err) {
      setResetSuccess(false);
      setErrorMessage(
        err?.data?.message || err?.error || "Password reset failed."
      );
    }
  };

  const strengthScore = getPasswordStrength(password);

  return (
    <FormContainer>
      <title>Reset Password | ShotArc</title>
      <div className="auth-page-wrap">
        <div className="auth-panel">
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">
            Choose a new password for your account and confirm it below.
          </p>

          {isLoading && <Loader />}

          {!isLoading && !resetSuccess && (
            <Form noValidate onSubmit={handleSubmit}>
              {errorMessage && (
                <Alert className="app-empty-state app-empty-state--danger mb-3">
                  {errorMessage}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter new password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  autoComplete="new-password"
                />
                {password && (
                  <ProgressBar
                    now={strengthScore}
                    variant={getPasswordVariant(strengthScore)}
                    className="mt-2"
                  />
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm new password"
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  autoComplete="new-password"
                />
              </Form.Group>

              <Button
                type="submit"
                variant="warning"
                className="text-dark fw-semibold auth-submit-btn w-100"
              >
                Reset Password
              </Button>
            </Form>
          )}

          {!isLoading && resetSuccess && (
            <div className="d-flex flex-column align-items-center text-center">
              <p className="auth-verify-message mb-2">Password reset successfully.</p>
              <CiCircleCheck color="#22c55e" size="3.5rem" />
              <Link
                to="/LoginScreen"
                className="btn btn-warning text-dark fw-semibold auth-submit-btn mt-3 w-100"
              >
                Log In
              </Link>
            </div>
          )}
        </div>
      </div>
    </FormContainer>
  );
};

export default ResetForgottenPasswordScreen;
